import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Select, SelectOptionType, Tooltip, useToast } from "@aidkitorg/component-library";
import { type ProgramPhase, PROGRAM_PHASES } from "@aidkitorg/types/lib/programPhase";
import { assertUnreachable } from "@aidkitorg/types/lib/typechecks";
import { ConfigurationContext } from "../Context";
import { get_deployment, usePost } from "../API";

export const ProgramPhaseIndicator: React.FC = () => {
  const config = useContext(ConfigurationContext);
  const deployment = get_deployment();

  const [programPhase, setProgramPhase] = useState<ProgramPhase | null>(null);
  const [isShowingModal, setIsShowingModal] = useState(false);
  const [newPhase, setNewPhase] = useState<ProgramPhase | undefined>();
  const [isUpdatingPhase, setIsUpdatingPhase] = useState(false);

  const getProgramPhase = usePost('/program/get_program_phase');
  const updateProgramPhase = usePost('/program/update_program_phase');

  const { toast } = useToast();

  const showModal = useCallback(() => {
    setIsShowingModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsShowingModal(false);
    setNewPhase(undefined);
  }, []);

  const fetchProgramPhase = useCallback(async () => {
    const programPhase = await getProgramPhase({});

    if (programPhase) {
      setProgramPhase(programPhase);
    }
  }, [getProgramPhase]);

  useEffect(() => {
    fetchProgramPhase();
  }, []);

  const handleUpdatePhase = useCallback(async () => {
    if (!newPhase) {
      return;
    }

    setIsUpdatingPhase(true);

    const response = await updateProgramPhase({ phase: newPhase });

    if (response && !('error' in response)) {
      toast({ variant: 'success', title: `Program has been updated to ${newPhase}` });
      await fetchProgramPhase();
      closeModal();
    }

    setIsUpdatingPhase(false);
  }, [newPhase, updateProgramPhase, fetchProgramPhase, closeModal, toast]);

  const programPhaseDescription = useMemo(() => {
    const interactionText = config.roles?.includes('admin') ? " Click to change this program's current phase." : '';

    switch (programPhase) {
      case null:
        return '';
      case 'live':
        return `This program is live and has access to all functionality.${interactionText}`;
      case 'testing':
        return `This program is in a testing phase. It cannot trigger payments or send messages except to the users of the program or AidKit employees.${interactionText}`;
      case 'ended':
        return `This program has ended. It cannot trigger payments or notifications. Cron jobs and Roboscreener will no longer run in this program.${interactionText}`;
      default:
        assertUnreachable(programPhase);
    }
  }, [programPhase, config]);

  const capitalize = useCallback((programPhase: ProgramPhase) => {
    return programPhase[0].toUpperCase() + programPhase.slice(1);
  }, []);

  const selectOptionForPhase = useCallback((programPhase: ProgramPhase) => {
    return {
      label: capitalize(programPhase),
      value: programPhase,
    };
  }, [capitalize]);

  const selectedNewPhaseOption = useMemo(() => {
    return newPhase ? selectOptionForPhase(newPhase) : undefined;
  }, [selectOptionForPhase, newPhase]);

  const newPhaseOptions = useMemo(() => {
    return PROGRAM_PHASES.filter((phase) => phase !== programPhase).map(selectOptionForPhase);
  }, [programPhase, selectOptionForPhase]);

  const handleSelectNewPhase = useCallback((newPhaseOption: SelectOptionType<ProgramPhase>) => {
    setNewPhase(newPhaseOption.value);
  }, []);

  const programPhaseSelectionWarning = useMemo(() => {
    const warningPrefix = `Switching this program to ${newPhase} will`;
    switch (newPhase) {
      case null:
        return '';
      case 'testing':
        return `${warningPrefix} only allow internal comms and payments testing`;
      case 'live':
        return `${warningPrefix} enable all payments and notifications`;
      case 'ended':
        return `${warningPrefix} disable all payments, notifications, and computations`;
    }
  }, [newPhase]);

  return (
    <div className='flex items-center'>
      {programPhase && (
        <Tooltip contentClassName='max-w-80' side='bottom' text={programPhaseDescription} asChild>
          {config.roles?.includes('admin')
            ? <Button variant='unstyled' className='underline decoration-dotted pr-0 pl-2' onClick={showModal}>{capitalize(programPhase)}</Button>
            : <span className='underline decoration-dotted pl-2 text-sm'>{capitalize(programPhase)}</span>
          }
        </Tooltip>
      )}
      <Modal open={isShowingModal} onClose={closeModal} title={`Update ${deployment} Phase`}>
        Updating the phase of {deployment} will have an immediate impact on program capabilities. Please proceed with caution.

        <div className='py-3'>
          <Select
            description='Select the new phase for this program'
            label='New phase'
            value={selectedNewPhaseOption}
            options={newPhaseOptions}
            onChange={handleSelectNewPhase}  
          />
        </div>

        {programPhaseSelectionWarning && (
          <Alert className='mb-3' variant='warning'>{programPhaseSelectionWarning}</Alert>
        )}

        <div className='flex justify-end gap-2'>
          <Button variant='secondary' onClick={closeModal}>
            Cancel
          </Button>

          <Button variant='primary' isDisabled={!newPhase || isUpdatingPhase} onClick={handleUpdatePhase} loading={isUpdatingPhase}>
            Update Phase
          </Button>
        </div>
      </Modal>
    </div>
  );
};
